import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import type { Id } from '../../../convex/_generated/dataModel';

interface AttendanceFilters {
  fromDate?: string;
  toDate?: string;
  coachUserId?: string;
  playerId?: string;
  sessionType?: 'group' | 'private';
}

export function useAttendanceReport(filters: AttendanceFilters) {
  const args: Record<string, unknown> = {};
  if (filters.fromDate) args.fromDate = filters.fromDate;
  if (filters.toDate) args.toDate = filters.toDate;
  if (filters.coachUserId) args.coachUserId = filters.coachUserId as Id<"users">;
  if (filters.playerId) args.playerId = filters.playerId as Id<"players">;
  if (filters.sessionType) args.sessionType = filters.sessionType;

  const data = useQuery(api.director.attendanceReports.get, args);
  return { data, isLoading: data === undefined };
}

export function useReportCoaches() {
  const data = useQuery(api.director.attendanceReports.getCoaches);
  return { data, isLoading: data === undefined };
}

export function useReportPlayers() {
  const data = useQuery(api.director.attendanceReports.getPlayers);
  return { data, isLoading: data === undefined };
}

/**
 * Export attendance records to CSV and trigger download.
 */
export function exportAttendanceCSV(
  rawRecords: {
    sessionDate: string;
    startTime: string;
    sessionType: string | null;
    playerName: string;
    status: string;
    coachName: string | null;
  }[]
) {
  const headers = ['Date', 'Time', 'Session Type', 'Player', 'Status', 'Coach'];
  const rows = rawRecords.map((r) => [
    r.sessionDate,
    r.startTime,
    r.sessionType ?? '',
    r.playerName,
    r.status,
    r.coachName ?? '',
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map((row) =>
      row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ),
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `attendance-report-${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}
